import { AlertTriangle, Mail, Phone, Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBookingRequestNotes, useBookingRequests } from '@/hooks/useBookingRequests';
import { useExperiences } from '@/hooks/useExperiences';
import { usePartners } from '@/hooks/usePartners';
import { slotTimeRangeLabel } from '@/types/types_availabilitySlot';
import {
  bookingFreeCancellationPhrase,
  bookingNotesMaxLength,
  bookingPriceLabelMaxLength,
  bookingPriceLines,
  bookingPriceMaxLines,
  bookingPriceTotal,
  bookingReferenceMaxLength,
  bookingRequestEndReasonLabels,
  bookingRequestEndReasonsByStatus,
  bookingRequestStatusLabels,
  bookingRequestTransitions,
  cancellationReasonText,
  cleanBookingPriceLines,
  cleanBookingReference,
  hasPriceBreakdown,
  isCancellationChargeable,
  optionalServiceLines,
  optionalServicePriceLines,
  validateBookingPriceLines,
  validateBookingReference,
  type BookingPriceLineType,
  type BookingRequestEndReasonType,
  type BookingRequestStatusType,
  type BookingRequestType,
  type CancellationInitiatorType,
  type SetBookingRequestStatusRequestType,
} from '@/types/types_bookingRequest';
import { isNonRefundableBookingType, type ExperienceType } from '@/types/types_experience';
// TEMPORARY — see src/types/types_requestCode.ts. Delete with the Generate
// button and `bookingReferenceHint` below when /portal mints these itself.
import { generateRequestCode } from '@/types/types_requestCode';
import { callableErrorMessage, setBookingReference, setBookingRequestStatus } from '@/util/callable_functions';
import { formatCalendarDate, formatDate, formatDeadlineDayJst, formatDeadlineJst, formatJpy } from '@/util/util_format';

const statusPillClass: Record<BookingRequestStatusType, string> = {
  pending: 'tw:bg-amber-100 tw:text-amber-900',
  held: 'tw:bg-yellow-100 tw:text-yellow-900',
  confirmed: 'tw:bg-green-100 tw:text-green-800',
  cancelled: 'tw:bg-red-100 tw:text-red-800',
  withdrawn: 'tw:bg-gray-100 tw:text-gray-700',
  expired: 'tw:bg-gray-100 tw:text-gray-700',
  unavailable: 'tw:bg-gray-100 tw:text-gray-700',
};

/**
 * TEMPORARY — what the Generate button makes, said once.
 *
 * Here because an admin looking at `X68E-UKAMJ6` has no way to tell that the
 * `UK` is theirs to fix: an experience or partner missing its two characters is
 * the one failure this dialog reports that has to be repaired on another screen.
 *
 * Goes with the button. See src/types/types_requestCode.ts.
 */
const bookingReferenceHint = 'Generate makes a code from the date the request came in, the experience\u2019s '
  + 'two-character code and the partner\u2019s \u2014 or paste the code Portal gave this booking.';

// What each move is called where an admin is doing it, rather than what the
// status is called once it has happened. "Offer a date" and "Held" are the same
// transition read from opposite ends.
const actionLabels: Record<BookingRequestStatusType, string> = {
  held: 'Offer a date',
  confirmed: 'Confirm',
  cancelled: 'Cancel booking',
  withdrawn: 'Partner withdrew',
  unavailable: 'No availability',
  expired: '',
  pending: '',
};

// Moves an admin can make. 'expired' is absent because a hold lapsing belongs to
// the scheduled task; everything else comes straight off the shared transition
// table, so this list cannot drift from what the server will accept.
type AdminTargetType = Exclude<BookingRequestStatusType, 'pending' | 'expired'>;

function adminActions(request: BookingRequestType): AdminTargetType[] {
  const immediate = isNonRefundableBookingType(request.experienceBookingType ?? 'inquiry');
  return bookingRequestTransitions[request.status]
    .filter((s): s is AdminTargetType => s !== 'expired' && s !== 'pending')
    .filter((target) => {
      // A supplier who answers yes or no on the phone has no date to hold.
      if (target === 'held') return !immediate;
      // And one who *can* hold must be given the chance to, before the partner is
      // committed to a price it has not seen. The server refuses this shortcut
      // for anything but an immediate experience; hiding it here is so an admin
      // is not offered a button that would be rejected.
      if (target === 'confirmed' && request.status === 'pending') return immediate;
      return true;
    });
}

/** Whether this action is the phone-call confirmation that commits on the spot. */
function isImmediateConfirm(action: ActionStateType): boolean {
  return action.target === 'confirmed' && action.request.status === 'pending';
}

interface ActionStateType {
  request: BookingRequestType;
  target: AdminTargetType;
}

/**
 * One priced row while it is being typed.
 *
 * `amount` is a string rather than a number because that is what an input holds:
 * a box being cleared to retype it passes through empty, and a `0` shown there
 * instead is a price the admin did not mean. It becomes a number once, on the way
 * out — see `toPriceLines`.
 */
interface PriceRowType {
  label: string;
  amount: string;
  commissionable: boolean;
}

/** The typed rows as the shared type sees them: cleaned, and numbers again. */
function toPriceLines(rows: PriceRowType[]): BookingPriceLineType[] {
  return cleanBookingPriceLines(
    rows.map((row) => ({ label: row.label, amount: Number(row.amount || 0), commissionable: row.commissionable })),
  );
}

/**
 * The booking-request queue: every request a partner has submitted, newest first,
 * with whatever moves it can still make offered on its row.
 *
 * Nothing here writes to Firestore. Each move goes through
 * `setBookingRequestStatus`, which re-reads the request inside a transaction —
 * so two admins working the queue at once cannot both act on the same stale row,
 * and the second one is told what happened rather than silently overwriting it.
 */
export function PageBookingRequestList() {
  const { requests, pendingCount, loading, error } = useBookingRequests();
  // Only for the catalogue price — what a new offer starts at, and what the
  // price boxes are captioned with. The queue itself lists requests, and
  // everything else it shows about the experience is already denormalised onto
  // them.
  const { experiences } = useExperiences();
  // TEMPORARY — only the two-character code, for minting a request code in the
  // reference dialog. Nothing else on this screen needs the partner list: every
  // other partner fact a row shows is denormalised onto the request.
  const { partners } = usePartners();
  // The private half of the notes, which the requests themselves cannot carry:
  // a partner can read its own request document, so anything it must not see lives
  // in a collection of its own. See `coll_bookingRequestNotes`.
  const notes = useBookingRequestNotes();
  const [action, setAction] = useState<ActionStateType | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** A transition that succeeded but left something for the admin to do. */
  const [notice, setNotice] = useState<string | null>(null);

  // Editing /portal's reference for one booking. Its own state rather than a
  // target on `action` above, because it is not a transition: it commits on its
  // own, through its own callable, and can be done to a request in any state.
  const [referenceFor, setReferenceFor] = useState<BookingRequestType | null>(null);
  const [reference, setReference] = useState('');
  const [referenceSaving, setReferenceSaving] = useState(false);
  const [referenceError, setReferenceError] = useState<string | null>(null);

  // Offer form.
  const [confirmedDate, setConfirmedDate] = useState('');
  const [priceRows, setPriceRows] = useState<PriceRowType[]>([]);
  const [heldUntil, setHeldUntil] = useState('');
  const [publicNotes, setPublicNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // Cancellation form.
  const [initiator, setInitiator] = useState<CancellationInitiatorType>('client');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationFee, setCancellationFee] = useState('');
  /** Ticked where a fee is due and the desk is choosing not to take it. */
  const [waiveFee, setWaiveFee] = useState(false);

  // Withdrawal / no-availability form.
  const [endReason, setEndReason] = useState<BookingRequestEndReasonType>('client-withdrew');

  /**
   * Ticked before an immediate confirmation, and the reason this dialog exists
   * as more than a button. Once the artisan has been told yes there is no
   * releasing the date, so the admin says out loud that they checked the partner
   * still wants it — before they pick up the phone, not after.
   */
  const [acknowledged, setAcknowledged] = useState(false);

  function openAction(request: BookingRequestType, target: AdminTargetType) {
    setAction({ request, target });
    setSaveError(null);

    // The first choice is the date the partner would rather have, and the one most
    // offers land on.
    setConfirmedDate(request.confirmedDate || request.preferredDates[0] || '');
    setPriceRows(suggestedPriceRows(request, experiences));
    setHeldUntil(defaultHoldDeadline());

    // Re-offering starts from what the last offer said rather than from blank:
    // the note usually still applies, and retyping it is how it comes to differ
    // from the one already sent.
    setPublicNotes(request.publicNotes ?? '');
    setInternalNotes(notes.get(request.id)?.internalNotes ?? '');

    setAcknowledged(false);
    setInitiator(target === 'cancelled' ? 'client' : 'admin');
    setCancellationReason('');
    setCancellationFee(request.agreedPrice ? String(request.agreedPrice) : '');
    setWaiveFee(false);
    setEndReason(bookingRequestEndReasonsByStatus[target === 'unavailable' ? 'unavailable' : 'withdrawn'][0]);
  }

  // Keyed off who ended it, which is the whole of what decides a fee — the reason
  // beside it is prose. Waiving does not change this: the fee is still due, and
  // saying so is what makes the tick mean something.
  const chargeable = action?.target === 'cancelled'
    && isCancellationChargeable(initiator, {
      now: new Date().toISOString(),
      freeCancellationUntil: action.request.freeCancellationUntil,
    });
  // The window that decided it, in the same words the partner was given — a day
  // for a deadline that runs to the end of one, an instant for a window frozen
  // under an earlier policy that stops part-way through a day.
  const freeCancellationPhrase = action
    && bookingFreeCancellationPhrase(action.request, { day: formatDeadlineDayJst, instant: formatDeadlineJst });

  function buildPayload(state: ActionStateType): SetBookingRequestStatusRequestType | string {
    const requestId = state.request.id;
    switch (state.target) {
      case 'held': {
        if (!confirmedDate) return 'Choose which date the supplier agreed to.';
        const priceLines = toPriceLines(priceRows);
        // The same check the Cloud Function will run, off the same rows, so the
        // admin is told here rather than by a rejected call.
        const priceProblem = validateBookingPriceLines(priceLines);
        if (priceProblem) return priceProblem;
        if (!heldUntil) return 'Say how long the date is held for.';
        if (publicNotes.length > bookingNotesMaxLength) return 'The note to the partner is too long.';
        if (internalNotes.length > bookingNotesMaxLength) return 'The internal note is too long.';
        return {
          requestId,
          status: 'held',
          confirmedDate,
          priceLines,
          heldUntil: new Date(heldUntil).toISOString(),
          publicNotes,
          internalNotes,
        };
      }
      case 'confirmed': {
        // Confirming a hold inherits the date and price the offer already
        // carried. Confirming straight from 'pending' has neither, because no
        // offer was ever made — the admin is on the phone to the artisan.
        if (!isImmediateConfirm(state)) return { requestId, status: 'confirmed' };
        if (!confirmedDate) return 'Choose which date the supplier agreed to.';
        const priceLines = toPriceLines(priceRows);
        const priceProblem = validateBookingPriceLines(priceLines);
        if (priceProblem) return priceProblem;
        if (!acknowledged) return 'Please confirm you have checked the partner still wants this booking.';
        return { requestId, status: 'confirmed', confirmedDate, priceLines };
      }
      case 'cancelled': {
        const reason = cancellationReason.trim();
        if (!reason) return 'Say why this booking is being cancelled.';
        if (reason.length > bookingNotesMaxLength) return 'The reason is too long.';
        const waived = chargeable && waiveFee;
        const fee = chargeable && !waived ? Number(cancellationFee) : 0;
        if (chargeable && !waived && (!Number.isInteger(fee) || fee < 0)) return 'Give the fee in whole yen.';
        return { requestId, status: 'cancelled', initiator, reason, cancellationFee: fee, waiveFee: waived };
      }
      case 'withdrawn':
      case 'unavailable':
        return { requestId, status: state.target, reason: endReason };
    }
  }

  async function save() {
    if (!action) return;
    const payload = buildPayload(action);
    if (typeof payload === 'string') {
      setSaveError(payload);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const res = await setBookingRequestStatus(payload);
      setAction(null);
      // The snapshot listener restyles the row.

      // Said here as well as on the row, because the admin who just held the
      // date is the person who can pick up the phone about it, and they are
      // looking at this screen right now.
      setNotice(
        res.data.offerSent === false
          ? 'The date is held, but the offer email could not be sent. Please call the partner — the deadline is running.'
          : null,
      );
    } catch (e) {
      console.error('Moving booking request failed', e);
      setSaveError(callableErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function openReference(request: BookingRequestType) {
    setReferenceFor(request);
    setReference(request.portalRequestCode ?? '');
    setReferenceError(null);
  }

  /**
   * TEMPORARY — mints a code and puts it in the box, without saving it.
   *
   * Deliberately not saved on the press: the admin sees what was made and
   * presses Save, which is the same button a pasted code goes through and the
   * same server-side uniqueness check. Pressing Generate again re-cuts it, so
   * nothing is committed by curiosity.
   *
   * Uniqueness is answered from the list this page already subscribes to rather
   * than by a query — every booking request is in memory here, which is what
   * makes the whole loop synchronous. The authority is still the callable: two
   * admins generating at once both see a free code, and the second Save is
   * refused. See src/types/types_requestCode.ts, and delete this with it.
   */
  async function generateReference() {
    if (!referenceFor) return;

    const experience = experiences.find((e) => e.id === referenceFor.experienceId);
    const partner = partners.find((p) => p.id === referenceFor.partnerId);
    if (!experience || !partner) {
      setReferenceError('The experience or partner for this booking could not be read. Try again.');
      return;
    }

    const result = await generateRequestCode(
      {
        // The day the request came in, so a code re-cut months later still says
        // when it was — the same date /portal keys its own codes off.
        dateIso: referenceFor.createdAt,
        experienceCode: experience.code,
        partnerCode: partner.code,
      },
      (code) => requests.some((r) => r.id !== referenceFor.id && r.portalRequestCode === code),
    );

    if (!result.ok) {
      setReferenceError(result.error);
      return;
    }
    setReference(result.code);
    setReferenceError(null);
  }

  async function saveReference() {
    if (!referenceFor) return;

    // Cleaned and checked with the same helpers the server will use, so a
    // reference refused there is one this box already objected to.
    const value = cleanBookingReference(reference);
    const problem = validateBookingReference(value);
    if (problem) {
      setReferenceError(problem);
      return;
    }

    setReferenceSaving(true);
    setReferenceError(null);
    try {
      await setBookingReference({ requestId: referenceFor.id, reference: value });
      setReferenceFor(null);
      // The snapshot listener redraws the cell.
    } catch (e) {
      console.error('Setting booking reference failed', e);
      setReferenceError(callableErrorMessage(e));
    } finally {
      setReferenceSaving(false);
    }
  }

  return (
    <div>
      <header className='tw:mb-6'>
        <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Booking Requests</h1>
        <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
          Experiences partners have asked us to hold for their guests.{' '}
          {pendingCount > 0 ? `${pendingCount} awaiting a decision.` : 'Nothing awaiting a decision.'}
        </p>
      </header>

      {error && (
        <p className='tw:mb-4 tw:rounded tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-4 tw:py-2.5 tw:text-sm tw:text-ed-red'>
          {error}
        </p>
      )}

      {notice && (
        <p className='tw:mb-4 tw:flex tw:items-start tw:gap-2 tw:rounded tw:border tw:border-ed-gold/40 tw:bg-ed-gold/10 tw:px-4 tw:py-2.5 tw:text-sm tw:text-ed-black'>
          <AlertTriangle className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-ed-red' />
          <span className='tw:flex-1'>{notice}</span>
          <button type='button' onClick={() => setNotice(null)} className='tw:text-xs tw:text-ed-stone tw:underline'>
            Dismiss
          </button>
        </p>
      )}

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr>
              {['Received', 'Reference', 'Partner', 'Experience', 'Guest', 'Dates', 'Notes', 'Status', ''].map((h) => (
                <th
                  key={h}
                  className='tw:px-6 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:tracking-wider tw:text-ed-stone tw:uppercase'
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  Loading…
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={9} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  No booking requests yet.
                </td>
              </tr>
            ) : (
              requests.map((request) => (
                <tr
                  key={request.id}
                  className='tw:border-t tw:border-ed-umber/10 tw:align-top tw:hover:bg-ed-cream/20'
                >
                  <td className='tw:px-6 tw:py-4 tw:text-sm tw:whitespace-nowrap'>
                    {formatDate(request.createdAt)}
                    {/* A request whose email never sent is the one case where
                        this list is the *only* place it exists — say so loudly,
                        because nobody is watching an inbox that got nothing. */}
                    {request.notifyError && (
                      <div
                        className='tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-red'
                        title={request.notifyError}
                      >
                        <AlertTriangle className='tw:size-3' /> Not emailed
                      </div>
                    )}
                    {/* A hold whose offer never reached the concierge is worse
                        than a lost email: the date is being kept for a partner
                        that does not know it has one, and the deadline is
                        running. Ring them.
                        On a direct booking the same field carries the failed
                        written confirmation, which is a different sentence —
                        the partner has the booking, just not in writing. */}
                    {request.offerEmailError && (
                      <div
                        className='tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-red'
                        title={request.offerEmailError}
                      >
                        <AlertTriangle className='tw:size-3' />
                        {request.confirmedVia === 'direct' ? 'Confirmation not sent' : 'Offer not sent'}
                      </div>
                    )}
                    {/* A confirmed booking /portal has not been told about is
                        ours to chase; nothing downstream will notice on its own. */}
                    {request.portalSyncState === 'pending' || request.portalSyncState === 'failed' ? (
                      <div className='tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-red' title={request.portalError}>
                        <AlertTriangle className='tw:size-3' /> Not in Portal
                      </div>
                    ) : null}
                  </td>
                  {/* /portal's code for this booking, and the button that puts
                      it there. Editable from the row rather than from a
                      transition dialog because that is when it is known: the
                      request is keyed into /portal after the fact, and whoever
                      does it comes back to this screen holding a code and
                      nothing else to change. */}
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    <button
                      type='button'
                      onClick={() => openReference(request)}
                      title='Set the /portal reference for this booking'
                      className={
                        request.portalRequestCode
                          ? 'tw:font-mono tw:text-xs tw:whitespace-nowrap tw:text-ed-black tw:underline tw:decoration-ed-umber/30 tw:underline-offset-4 tw:transition-colors tw:hover:text-ed-gold'
                          : 'tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase tw:transition-colors tw:hover:text-ed-gold'
                      }
                    >
                      {request.portalRequestCode || 'Add'}
                    </button>
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm tw:font-medium tw:text-ed-black'>
                    {request.partnerName}
                    <div className='tw:mt-0.5 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:font-normal tw:text-ed-stone'>
                      <Mail className='tw:size-3' />
                      <a href={`mailto:${request.requestedByEmail}`} className='tw:hover:text-ed-gold'>
                        {request.requestedByName || request.requestedByEmail}
                      </a>
                    </div>
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>{request.experienceName}</td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    {request.customerFirstName} {request.customerLastName}
                    <div className='tw:mt-0.5 tw:text-xs tw:text-ed-stone'>
                      {request.guests} {request.guests === 1 ? 'guest' : 'guests'}
                    </div>
                    <div className='tw:mt-0.5 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-stone'>
                      <Phone className='tw:size-3' /> {request.contactPhone}
                    </div>
                    {/* Above the special requests, since it is the half of the
                        ask that is already priced — and worth seeing while
                        skimming, because it is a supplier to arrange rather than
                        a note to read. */}
                    {!!request.optionalServices?.length && (
                      <ul className='tw:mt-2 tw:max-w-xs tw:text-xs tw:text-ed-charcoal/80'>
                        {optionalServiceLines(request.optionalServices, formatJpy).map((line) => (
                          <li key={line}>+ {line}</li>
                        ))}
                      </ul>
                    )}
                    {request.specialRequests && (
                      <p className='tw:mt-2 tw:max-w-xs tw:text-xs tw:italic tw:text-ed-charcoal/70'>
                        “{request.specialRequests}”
                      </p>
                    )}
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    <ul className='tw:space-y-0.5'>
                      {request.preferredDates.map((date) => {
                        const held = date === request.confirmedDate;
                        return (
                          <li
                            key={date}
                            className={
                              held ? 'tw:font-medium tw:text-ed-black' : 'tw:whitespace-nowrap tw:text-ed-charcoal/80'
                            }
                          >
                            {formatCalendarDate(date)}
                            {/* The hour, on a booking that has one — a slot taken
                                off the calendar is the artisan's actual sitting,
                                and it is what the desk has to tell them. */}
                            {held && request.confirmedStartTime && (
                              <span className='tw:ml-2 tw:whitespace-nowrap tw:text-ed-charcoal/80'>
                                {slotTimeRangeLabel({
                                  startTime: request.confirmedStartTime,
                                  endTime: request.confirmedEndTime ?? '',
                                })}
                              </span>
                            )}
                            {held && (
                              <span className='tw:ml-2 tw:text-xs tw:text-green-700'>
                                {request.status === 'confirmed' ? 'booked' : 'offered'}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {/* How this booking came to exist, where it was not us: a
                        direct booking arrived already confirmed, off the
                        calendar, with no decision from the desk at all. */}
                    {request.confirmedVia === 'direct' && (
                      <div className='tw:mt-1 tw:text-xs tw:text-ed-stone'>Booked from the calendar</div>
                    )}
                    {request.agreedPrice != null && (
                      <div className='tw:mt-1 tw:text-xs tw:text-ed-stone'>
                        ¥{request.agreedPrice.toLocaleString()}
                        {/* What made up the total, where it was more than the
                            experience. Marked where a line earns the partner
                            nothing, because that is the part of an offer the desk
                            is asked about after the invoice goes out. */}
                        {hasPriceBreakdown(request) && (
                          <ul className='tw:mt-0.5'>
                            {bookingPriceLines(request).map((line, i) => (
                              <li key={i}>
                                {line.label} ¥{line.amount.toLocaleString()}
                                {!line.commissionable && ' · no commission'}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Both notes, labelled by who may read them. Kept apart on
                      the row rather than run together, because the whole point
                      of the pair is that one of them was sent to the partner and
                      the other was not — an admin skimming the queue has to be
                      able to tell at a glance which sentence is which. */}
                  <td className='tw:px-6 tw:py-4 tw:text-xs'>
                    {request.publicNotes && (
                      <div className='tw:max-w-56 tw:whitespace-pre-line tw:text-ed-charcoal'>
                        <span className='tw:text-ed-stone'>To partner:</span> {request.publicNotes}
                      </div>
                    )}
                    {notes.get(request.id)?.internalNotes && (
                      <div
                        className={`tw:max-w-56 tw:whitespace-pre-line tw:text-ed-charcoal ${request.publicNotes ? 'tw:mt-2' : ''}`}
                      >
                        <span className='tw:text-ed-stone'>Internal:</span>{' '}
                        {notes.get(request.id)?.internalNotes}
                      </div>
                    )}
                  </td>
                  <td className='tw:px-6 tw:py-4'>
                    <span
                      className={`tw:rounded-full tw:px-2 tw:py-1 tw:text-xs tw:whitespace-nowrap ${statusPillClass[request.status]}`}
                    >
                      {bookingRequestStatusLabels[request.status]}
                    </span>
                    {request.status !== 'pending' && (
                      <div className='tw:mt-1 tw:text-xs tw:text-ed-stone'>{formatDate(request.statusChangedAt)}</div>
                    )}
                    {request.status === 'held' && request.heldUntil && (
                      <div className='tw:mt-1 tw:text-xs tw:text-ed-stone'>
                        until {formatDate(request.heldUntil)}
                      </div>
                    )}
                    {request.endReason && (
                      <div className='tw:mt-1 tw:max-w-40 tw:text-xs tw:text-ed-stone'>
                        {bookingRequestEndReasonLabels[request.endReason]}
                      </div>
                    )}
                    {request.status === 'cancelled' && cancellationCellText(request) && (
                      <div className='tw:mt-1 tw:max-w-40 tw:text-xs tw:text-ed-stone'>
                        {cancellationCellText(request)}
                      </div>
                    )}
                  </td>
                  <td className='tw:px-6 tw:py-4'>
                    <div className='tw:flex tw:flex-col tw:items-end tw:gap-1.5'>
                      {adminActions(request).map((target) => (
                        <button
                          key={target}
                          type='button'
                          onClick={() => openAction(request, target)}
                          className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase tw:transition-colors tw:hover:text-ed-gold'
                        >
                          {actionLabels[target]}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!action} onOpenChange={(open) => !open && setAction(null)}>
        <DialogContent className='tw:sm:max-w-3xl'>
          {action && (
            <>
              <DialogHeader>
                <DialogTitle>{actionLabels[action.target]}</DialogTitle>
                {/* The party size sits with the guest's name rather than beside
                    the price boxes, because it is a fact about the booking
                    rather than a field of this form — but it is here at all
                    because pricing is the moment it matters: a figure per head
                    and a figure for the group are the same box.
                    The two dialogs that ring a supplier say all of this at
                    length in the recap below, so there the description only has
                    to say what the form does. */}
                <DialogDescription>
                  {action.target === 'held' ? (
                    'What the partner is sent: the date, the price and your note to them.'
                  ) : isImmediateConfirm(action) ? (
                    'The date is booked the moment you press the button — there is no offer to withdraw.'
                  ) : (
                    <>
                      {action.request.experienceName} for {action.request.customerFirstName}{' '}
                      {action.request.customerLastName}, {action.request.guests}{' '}
                      {action.request.guests === 1 ? 'guest' : 'guests'}, requested by{' '}
                      {action.request.partnerName}.
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              {action.target === 'held' && (
                <div className='tw:space-y-4'>
                  <RequestRecap request={action.request} />

                  <div>
                    <p className='tw:mb-2 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>
                      Date the supplier agreed to
                    </p>
                    {/* Radio inputs rather than the RadioGroup component: this
                        sits in a dialog whose content is a grid, which that
                        component is documented to render badly inside. */}
                    <div className='tw:space-y-2'>
                      {action.request.preferredDates.map((date, i) => (
                        <label key={date} className='tw:flex tw:items-center tw:gap-3 tw:text-sm'>
                          <input
                            type='radio'
                            name='confirmedDate'
                            value={date}
                            checked={confirmedDate === date}
                            onChange={() => setConfirmedDate(date)}
                            className='tw:size-4 tw:accent-ed-gold'
                          />
                          <span>
                            {formatCalendarDate(date)}
                            {i === 0 && <span className='tw:ml-2 tw:text-xs tw:text-ed-stone'>first choice</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <PriceRowsEditor
                    rows={priceRows}
                    onChange={setPriceRows}
                    basePrice={experienceBasePrice(action.request, experiences)}
                  />

                  <label className='tw:block'>
                    <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Held until</span>
                    <input
                      type='datetime-local'
                      value={heldUntil}
                      onChange={(e) => setHeldUntil(e.target.value)}
                      className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
                    />
                    <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                      The partner is told this deadline, and the hold is released automatically once it
                      passes.
                    </span>
                  </label>

                  <label className='tw:block'>
                    <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>
                      Note to the partner
                    </span>
                    <textarea
                      rows={3}
                      maxLength={bookingNotesMaxLength}
                      value={publicNotes}
                      onChange={(e) => setPublicNotes(e.target.value)}
                      placeholder='What the artisan asks of the guests, or anything the concierge should know.'
                      className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
                    />
                    <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                      Sent with the offer email, and shown on the partner&rsquo;s copy of the booking.
                    </span>
                  </label>

                  <label className='tw:block'>
                    <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Internal note</span>
                    <textarea
                      rows={3}
                      maxLength={bookingNotesMaxLength}
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      placeholder='For the desk. What the artisan said on the phone, what to chase.'
                      className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
                    />
                    {/* Said plainly, because a note written in the wrong box
                        cannot be unsent — and this is stored where the partner
                        cannot reach it, not merely hidden from their screens. */}
                    <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                      Eighty Days only. Never emailed, and the partner cannot read it.
                    </span>
                  </label>
                </div>
              )}

              {action.target === 'confirmed' && !isImmediateConfirm(action) && (
                <p className='tw:text-sm tw:text-ed-charcoal'>
                  Books {formatCalendarDate(action.request.confirmedDate ?? '')}
                  {action.request.agreedPrice != null && ` at ¥${action.request.agreedPrice.toLocaleString()}`}. The
                  booking will not reach Portal until that integration is built.
                </p>
              )}

              {action.target === 'confirmed' && isImmediateConfirm(action) && (
                <div className='tw:space-y-4'>
                  <p className='tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-3 tw:py-2.5 tw:text-sm tw:text-ed-charcoal'>
                    <strong>This booking cannot be cancelled free of charge.</strong> This supplier
                    answers yes or no on the call and is owed for the date from the moment they
                    agree, so the partner pays in full for any cancellation afterwards.
                  </p>

                  {/* Under the warning rather than over it: the admin should
                      read what this button costs before the booking it is
                      about. */}
                  <RequestRecap request={action.request} />

                  <div>
                    <p className='tw:mb-2 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>
                      Date the supplier agreed to
                    </p>
                    <div className='tw:space-y-2'>
                      {action.request.preferredDates.map((date, i) => (
                        <label key={date} className='tw:flex tw:items-center tw:gap-3 tw:text-sm'>
                          <input
                            type='radio'
                            name='immediateConfirmedDate'
                            value={date}
                            checked={confirmedDate === date}
                            onChange={() => setConfirmedDate(date)}
                            className='tw:size-4 tw:accent-ed-gold'
                          />
                          <span>
                            {formatCalendarDate(date)}
                            {i === 0 && <span className='tw:ml-2 tw:text-xs tw:text-ed-stone'>first choice</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <PriceRowsEditor
                    rows={priceRows}
                    onChange={setPriceRows}
                    basePrice={experienceBasePrice(action.request, experiences)}
                  />

                  <label className='tw:flex tw:items-start tw:gap-3 tw:text-sm tw:text-ed-charcoal'>
                    <input
                      type='checkbox'
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      className='tw:mt-0.5 tw:size-4 tw:accent-ed-gold'
                    />
                    <span>
                      I have checked with the partner that this booking is still wanted, before calling
                      the supplier.
                    </span>
                  </label>
                </div>
              )}

              {action.target === 'cancelled' && (
                <div className='tw:space-y-4'>
                  {/* First, because it is the only field here that decides money:
                      the fee below appears or does not according to this answer. */}
                  <label className='tw:block'>
                    <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Who ended it</span>
                    <select
                      value={initiator}
                      onChange={(e) => setInitiator(e.target.value as CancellationInitiatorType)}
                      className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
                    >
                      <option value='client'>The partner</option>
                      <option value='supplier'>The supplier</option>
                      <option value='admin'>Eighty Days</option>
                    </select>
                    <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                      Only the partner&rsquo;s own cancellation can carry a fee, and only after the free
                      window has closed.
                    </span>
                  </label>

                  <label className='tw:block'>
                    <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Reason</span>
                    <textarea
                      rows={3}
                      maxLength={bookingNotesMaxLength}
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      placeholder='What happened, in your own words. The artisan fell ill, the guest changed plans.'
                      className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
                    />
                    {/* Said plainly, because the old dropdown's wording did go to
                        the partner and this box does not. */}
                    <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                      Kept on the booking and sent to the Eighty Days desk. The partner is told the booking
                      was cancelled, never what is written here.
                    </span>
                  </label>

                  {/* Cancelling a direct booking hands the artisan's hour back to
                      the calendar, where another partner can take it. Right most of
                      the time and wrong for a late cancellation the artisan cannot
                      rework, so the admin is told rather than left to find out. */}
                  {action.request.slotId && (
                    <p className='tw:border tw:border-ed-gold/40 tw:bg-ed-gold/10 tw:px-3 tw:py-2.5 tw:text-sm tw:text-ed-charcoal'>
                      This booking came off the calendar. Cancelling it puts{' '}
                      {formatCalendarDate(action.request.confirmedDate)} back on sale — delete the slot on the
                      experience if the artisan cannot rework the time.
                    </p>
                  )}

                  {chargeable ? (
                    <div className='tw:space-y-3'>
                      <label className='tw:block'>
                        <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Fee (¥)</span>
                        <input
                          type='number'
                          min={0}
                          step={1}
                          disabled={waiveFee}
                          value={waiveFee ? '0' : cancellationFee}
                          onChange={(e) => setCancellationFee(e.target.value)}
                          className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm tw:disabled:bg-ed-cream/50 tw:disabled:text-ed-stone'
                        />
                      </label>

                      {/* Offered only where a fee is actually due, which is the only
                          place waiving one means anything — and recorded as a waiver
                          rather than as a ¥0, so next month's invoice and the desk
                          can tell a favour from a free cancellation. */}
                      <label className='tw:flex tw:items-start tw:gap-3 tw:text-sm tw:text-ed-charcoal'>
                        <input
                          type='checkbox'
                          checked={waiveFee}
                          onChange={(e) => setWaiveFee(e.target.checked)}
                          className='tw:mt-0.5 tw:size-4 tw:accent-ed-gold'
                        />
                        <span>
                          Waive the cancellation fee. The partner is told it has been waived and is charged
                          nothing.
                        </span>
                      </label>
                    </div>
                  ) : (
                    <p className='tw:text-xs tw:text-ed-stone'>
                      No fee applies
                      {initiator === 'client' && freeCancellationPhrase
                        && ` — free cancellation runs until ${freeCancellationPhrase}`}
                      .
                    </p>
                  )}
                </div>
              )}

              {(action.target === 'withdrawn' || action.target === 'unavailable') && (
                <label className='tw:block'>
                  <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Why</span>
                  <select
                    value={endReason}
                    onChange={(e) => setEndReason(e.target.value as BookingRequestEndReasonType)}
                    className='tw:mt-1 tw:w-full tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
                  >
                    {bookingRequestEndReasonsByStatus[action.target].map((value) => (
                      <option key={value} value={value}>
                        {bookingRequestEndReasonLabels[value]}
                      </option>
                    ))}
                  </select>
                  <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                    Nothing is emailed to the partner from here yet.
                  </span>
                </label>
              )}

              {saveError && <p className='tw:text-sm tw:text-ed-red'>{saveError}</p>}

              <DialogFooter>
                <Button variant='outline' disabled={saving} onClick={() => setAction(null)}>
                  Cancel
                </Button>
                <Button
                  variant={action.target === 'confirmed' || action.target === 'held' ? 'default' : 'destructive'}
                  loading={saving}
                  onClick={() => void save()}
                >
                  {saving ? 'Saving…' : actionLabels[action.target]}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* A dialog of its own rather than a box on the row, because the value is
          long, easy to mistype and worth reading back against the booking it is
          being matched to — which is why the header names that booking. */}
      <Dialog open={!!referenceFor} onOpenChange={(open) => !open && setReferenceFor(null)}>
        <DialogContent className='tw:sm:max-w-md'>
          {referenceFor && (
            <>
              <DialogHeader>
                <DialogTitle>Booking reference</DialogTitle>
                <DialogDescription>
                  {referenceFor.experienceName} for {referenceFor.customerFirstName}{' '}
                  {referenceFor.customerLastName}, requested by {referenceFor.partnerName}.
                </DialogDescription>
              </DialogHeader>

              <label className='tw:block'>
                <span className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>
                  Request code
                </span>
                <div className='tw:mt-1 tw:flex tw:items-stretch tw:gap-2'>
                  <input
                    type='text'
                    value={reference}
                    maxLength={bookingReferenceMaxLength}
                    autoFocus
                    onChange={(e) => setReference(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void saveReference()}
                    className='tw:min-w-0 tw:flex-1 tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:font-mono tw:text-sm'
                  />
                  {/* TEMPORARY — goes when /portal mints these itself. See
                      src/types/types_requestCode.ts. */}
                  <Button type='button' variant='outline' onClick={() => void generateReference()}>
                    Generate
                  </Button>
                </div>
                <span className='tw:mt-1 tw:block tw:text-xs tw:text-ed-stone'>
                  {bookingReferenceHint} Leave it empty to remove a reference entered against the wrong
                  booking. Nothing is emailed from here, and the booking itself does not change.
                </span>
              </label>

              {referenceError && <p className='tw:text-sm tw:text-ed-red'>{referenceError}</p>}

              <DialogFooter>
                <Button variant='outline' disabled={referenceSaving} onClick={() => setReferenceFor(null)}>
                  Cancel
                </Button>
                <Button loading={referenceSaving} onClick={() => void saveReference()}>
                  {referenceSaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The cancellation, in the width of a status cell: the desk's own words, and what
 * it cost. Empty where a booking was cancelled before either was recorded, so the
 * caller can leave the line out rather than print a stray separator.
 */
function cancellationCellText(request: BookingRequestType): string {
  const parts = [cancellationReasonText(request.cancellationReason)];
  if (request.cancellationFee) parts.push(`¥${request.cancellationFee.toLocaleString()} fee`);
  else if (request.cancellationFeeWaived) parts.push('fee waived');
  return parts.filter(Boolean).join(' · ');
}

/** One labelled fact in the recap. Omitted entirely when there is nothing to say. */
function RecapField({ label, children }: { label: string; children: ReactNode }) {
  if (children == null || children === '') return null;
  return (
    <div>
      <dt className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>{label}</dt>
      <dd className='tw:mt-0.5 tw:text-sm tw:text-ed-black'>{children}</dd>
    </div>
  );
}

/**
 * Everything the partner asked for, restated above the two forms that ring a
 * supplier: offering a date, and confirming one on the call.
 *
 * Both are priced and dated against these facts, and the admin working them is
 * usually on the phone to the artisan while they read them — so the party size,
 * the special requests and the number to ring the concierge back on are all here
 * rather than behind a closed dialog on the row beneath. Nothing in it is
 * editable: this is the request as submitted, and the boxes below are the only
 * things this screen changes.
 */
function RequestRecap({ request }: { request: BookingRequestType }) {
  return (
    <dl className='tw:grid tw:gap-x-6 tw:gap-y-3 tw:border tw:border-ed-umber/20 tw:bg-ed-cream/30 tw:px-4 tw:py-3.5 tw:sm:grid-cols-2'>
      <RecapField label='Experience'>{request.experienceName}</RecapField>

      <RecapField label='Guests'>
        {request.guests} {request.guests === 1 ? 'guest' : 'guests'}
        {(request.customerFirstName || request.customerLastName) && (
          <span className='tw:text-ed-charcoal/80'>
            {' · '}
            {request.customerFirstName} {request.customerLastName}
          </span>
        )}
      </RecapField>

      {/* The partner, then the person there who submitted it: a reply goes to the
          concierge by name, and the commission goes to the partner. */}
      <RecapField label='Requested by'>
        {request.partnerName}
        <div className='tw:mt-0.5 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-stone'>
          <Mail className='tw:size-3 tw:shrink-0' />
          <a href={`mailto:${request.requestedByEmail}`} className='tw:break-all tw:hover:text-ed-gold'>
            {request.requestedByName ? `${request.requestedByName} · ${request.requestedByEmail}` : request.requestedByEmail}
          </a>
        </div>
        <div className='tw:mt-0.5 tw:text-xs tw:text-ed-stone'>{formatDate(request.createdAt)}</div>
      </RecapField>

      {/* The guest's own number, and the reason this dialog can be worked
          without leaving it: an artisan who wants to ask something is answered
          from here rather than from the row underneath. */}
      <RecapField label='Contact phone'>
        <a href={`tel:${request.contactPhone}`} className='tw:hover:text-ed-gold'>
          {request.contactPhone}
        </a>
      </RecapField>

      {/* The extras the partner ticked, at the price they were shown. Beside the
          request rather than only in the price boxes below, because these are
          suppliers to ring as well as rows to charge — and because the boxes are
          editable, so what is quoted there is no longer evidence of what was
          actually asked for. */}
      {!!request.optionalServices?.length && (
        <div className='tw:sm:col-span-2'>
          <dt className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Additional services</dt>
          <dd className='tw:mt-0.5 tw:text-sm tw:text-ed-black'>
            {optionalServiceLines(request.optionalServices, formatJpy).join(' · ')}
          </dd>
        </div>
      )}

      {/* Last and across the width, because it is prose rather than a field, and
          because it is the line most likely to change what the supplier is
          asked for. Said plainly where there is none, so a blank cannot be read
          as "not shown here". */}
      <div className='tw:sm:col-span-2'>
        <dt className='tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>Special requests</dt>
        <dd
          className={
            request.specialRequests
              ? 'tw:mt-0.5 tw:whitespace-pre-line tw:text-sm tw:text-ed-black'
              : 'tw:mt-0.5 tw:text-sm tw:text-ed-stone'
          }
        >
          {request.specialRequests || 'None'}
        </dd>
      </div>
    </dl>
  );
}

/**
 * What a booking costs, itemised: the experience, and whatever the desk arranged
 * around it.
 *
 * One row is the ordinary case and the editor looks like one price box with a
 * checkbox beside it. The rest is for the date that comes with a car, an
 * interpreter or a lunch — things we arrange at cost and the partner earns nothing
 * on, which is what the checkbox is for. It defaults on for the experience and
 * off for everything added afterwards, because that is what is true nearly every
 * time, and it is a tick either way when it is not.
 *
 * The total is shown rather than typed. It is what the partner is asked to agree to
 * and what the invoice is grossed at, and an admin adding a ¥15,000 transfer to a
 * ¥40,000 workshop should not also have to do the sum.
 */
function PriceRowsEditor({
  rows,
  onChange,
  basePrice,
}: {
  rows: PriceRowType[];
  onChange: (rows: PriceRowType[]) => void;
  basePrice: number;
}) {
  const total = bookingPriceTotal(toPriceLines(rows));

  function patch(index: number, changes: Partial<PriceRowType>) {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...changes } : row)));
  }

  return (
    <div>
      {/* The catalogue price beside the boxes, because what is typed into them
          is a figure the artisan gave on the phone and the admin has to be able
          to see it drifting from what the experience is listed at. Per person,
          which is what `basePrice` is — the suggested first row is it times the
          party. */}
      <p className='tw:mb-2 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase'>
        Price
        <span className='tw:ml-2 tw:tracking-normal tw:normal-case'>
          {basePrice > 0
            ? `(Starting from ${formatJpy(basePrice)} per person)`
            : '(No catalogue price — on request)'}
        </span>
      </p>

      <div className='tw:space-y-2'>
        {rows.map((row, i) => (
          // Keyed by position because that is what a row is here: there is no id
          // to key by, and every input is driven from `rows` rather than holding
          // state of its own, so a removed row cannot leave a stale box behind.
          <div key={i} className='tw:flex tw:items-center tw:gap-2'>
            <input
              type='text'
              value={row.label}
              maxLength={bookingPriceLabelMaxLength}
              placeholder={i === 0 ? 'The experience' : 'Transfer, interpreter, lunch…'}
              onChange={(e) => patch(i, { label: e.target.value })}
              className='tw:min-w-0 tw:flex-1 tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
            />
            <input
              type='number'
              min={0}
              step={1}
              value={row.amount}
              onChange={(e) => patch(i, { amount: e.target.value })}
              className='tw:w-28 tw:border tw:border-ed-umber/30 tw:px-3 tw:py-2 tw:text-sm'
            />
            <label
              className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-ed-stone'
              title='The partner earns its commission on this line.'
            >
              <input
                type='checkbox'
                checked={row.commissionable}
                onChange={(e) => patch(i, { commissionable: e.target.checked })}
                className='tw:size-4 tw:accent-ed-gold'
              />
              Commission
            </label>
            {/* Never the last one: a booking with no priced line at all is not a
                cheaper offer, it is an offer with nothing in it. */}
            <button
              type='button'
              onClick={() => onChange(rows.filter((_, at) => at !== i))}
              disabled={rows.length === 1}
              aria-label='Remove this line'
              className='tw:text-ed-stone tw:transition-colors tw:hover:text-ed-red tw:disabled:opacity-30'
            >
              <X className='tw:size-4' />
            </button>
          </div>
        ))}
      </div>

      <div className='tw:mt-2 tw:flex tw:items-center tw:justify-between'>
        <button
          type='button'
          onClick={() => onChange([...rows, { label: '', amount: '', commissionable: false }])}
          disabled={rows.length >= bookingPriceMaxLines}
          className='tw:flex tw:items-center tw:gap-1 tw:text-xs tw:tracking-wider tw:text-ed-stone tw:uppercase tw:transition-colors tw:hover:text-ed-gold tw:disabled:opacity-40'
        >
          <Plus className='tw:size-3' /> Add a service
        </button>
        <span className='tw:text-sm tw:text-ed-black'>
          Total <strong>¥{total.toLocaleString()}</strong>
        </span>
      </div>

      <p className='tw:mt-1 tw:text-xs tw:text-ed-stone'>
        The partner is quoted every line and the total. Commission is deducted only from the ticked
        ones, so a service we arrange at cost earns them nothing.
      </p>
    </div>
  );
}

/**
 * What this experience is listed at, per person, or 0 where the catalogue says
 * "price on request". 0 as well for an experience the subscription has not
 * delivered yet — which reads the same here, since neither is a price to quote.
 */
function experienceBasePrice(request: BookingRequestType, experiences: ExperienceType[]): number {
  return experiences.find((e) => e.id === request.experienceId)?.basePrice ?? 0;
}

/**
 * What the price rows start at: whatever was already offered, else one row for
 * the experience at the catalogue price for this party — its base price times
 * the number of guests.
 *
 * A suggestion and nothing more. It is the figure the admin rings the supplier
 * with, and they type over it whenever the artisan says something else; what
 * gets held is what is in the boxes when they press the button. Nothing is
 * suggested where there is nothing to go on — a base price of 0 is the
 * catalogue saying "price on request", and an experience the subscription has
 * not delivered yet is not a price of zero.
 *
 * The extras the partner ticked come through as rows of their own, at the price
 * they were offered at and carrying their own commission flag. A suggestion like
 * everything else here — a shuttle the artisan turns out to include is deleted
 * rather than argued with — but it starts on the form, because a service asked
 * for and left off the offer is one the partner is never billed for and never
 * hears about again.
 */
function suggestedPriceRows(request: BookingRequestType, experiences: ExperienceType[]): PriceRowType[] {
  // Re-offering starts from the rows the last offer carried, the same way the
  // notes do: the transfer that was arranged for the old date is nearly always
  // still wanted for the new one.
  if (request.priceLines?.length) {
    return request.priceLines.map((line) => ({ ...line, amount: String(line.amount) }));
  }

  const basePrice = experienceBasePrice(request, experiences);
  const suggested = request.agreedPrice || (basePrice > 0 ? basePrice * request.guests : 0);
  return [
    { label: request.experienceName, amount: suggested ? String(suggested) : '', commissionable: true },
    ...optionalServicePriceLines(request.optionalServices ?? []).map((line) => ({
      ...line,
      amount: String(line.amount),
    })),
  ];
}

/**
 * The end of the day a week out, as a `datetime-local` value. Long enough that an
 * artisan's answer and a partner's reply can cross a weekend, short enough that a
 * date is not held out of the diary indefinitely. The deadline lands at 23:59 so
 * the partner gets the whole of the last day rather than losing the hold at
 * whatever hour the offer happened to be written.
 */
function defaultHoldDeadline(): string {
  const at = new Date();
  at.setDate(at.getDate() + 7);
  at.setHours(23, 59, 0, 0);
  at.setMinutes(at.getMinutes() - at.getTimezoneOffset());
  return at.toISOString().slice(0, 16);
}
